import classNames from "classnames";
import { MAX_RECOGNITION_COMPONENTS } from "@common/constants/domains/accounting/schedules";
import { isTermRecognitionMethod } from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { VStack } from "@design-system/VStack/VStack";
import { PlusIcon } from "@icons";
import { ScheduleManualRecognitionRow } from "./ScheduleManualRecognitionRow";
import { ScheduleDraft } from "./scheduleDraft";
import {
    addScheduleDraftRow,
    moveScheduleDraftRowDate,
    removeScheduleDraftRow,
    updateScheduleDraftRow,
} from "./scheduleDraftRows";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import { ScheduleRowLabels } from "./scheduleRowLabels";
import drawerStyles from "../ScheduleDrawer.module.scss";
import styles from "./ScheduleManualModal.module.scss";

export interface ScheduleManualRecognitionSectionProps {
    draft: ScheduleDraft;
    onDraftChange: (draft: ScheduleDraft) => void;
    /** False once the schedule can no longer change. */
    isEditable: boolean;
    areRowsLocked?: boolean;
    isSpecificDateEditable: boolean;
    /** Why the rows cannot be applied yet, shown under them. */
    errorMessage: string | null;
    minDate: string | undefined;
    portalContainer: HTMLElement | undefined;
    labels: ScheduleRowLabels;
    layout: ScheduleManualLayout;
}

export const ScheduleManualRecognitionSection = ({
    draft,
    onDraftChange,
    isEditable,
    areRowsLocked = false,
    isSpecificDateEditable,
    errorMessage,
    minDate,
    portalContainer,
    labels,
    layout,
}: ScheduleManualRecognitionSectionProps) => {
    const termMethodKey = draft.rows.find((row) =>
        isTermRecognitionMethod(row.method),
    )?.key;
    const canChangeRows = isEditable && !areRowsLocked;
    const canRemove = canChangeRows && draft.rows.length > 1;
    const isFramed = layout === "drawer";

    return (
        <VStack gap={8}>
            <div className={classNames({ [drawerStyles.card]: isFramed })}>
                <div
                    className={
                        isFramed ? drawerStyles.cardStack : styles.rowStack
                    }
                >
                    {draft.rows.map((row) => (
                        <ScheduleManualRecognitionRow
                            key={row.key}
                            labels={labels}
                            row={row}
                            isTermMethodTaken={
                                termMethodKey !== undefined &&
                                termMethodKey !== row.key
                            }
                            isReadOnly={!isEditable}
                            isMethodEditable={canChangeRows}
                            onAmountChange={(amount) =>
                                onDraftChange(
                                    updateScheduleDraftRow({
                                        draft,
                                        key: row.key,
                                        patch: { amount },
                                    }),
                                )
                            }
                            onMethodChange={(method) =>
                                onDraftChange(
                                    updateScheduleDraftRow({
                                        draft,
                                        key: row.key,
                                        patch: { method },
                                    }),
                                )
                            }
                            onDateChange={
                                !isEditable ||
                                (row.isSpecificDate && !isSpecificDateEditable)
                                    ? undefined
                                    : (date) =>
                                          onDraftChange(
                                              moveScheduleDraftRowDate({
                                                  draft,
                                                  key: row.key,
                                                  date,
                                              }),
                                          )
                            }
                            onRemove={
                                canRemove
                                    ? () =>
                                          onDraftChange(
                                              removeScheduleDraftRow({
                                                  draft,
                                                  key: row.key,
                                              }),
                                          )
                                    : undefined
                            }
                            term={{
                                length: draft.termLength,
                                unit: draft.termUnit,
                            }}
                            onTermChange={
                                isEditable
                                    ? ({ length, unit }) =>
                                          onDraftChange({
                                              ...draft,
                                              termLength: length,
                                              termUnit: unit,
                                          })
                                    : undefined
                            }
                            minDate={minDate}
                            portalContainer={portalContainer}
                        />
                    ))}
                </div>
                {canChangeRows &&
                    draft.rows.length < MAX_RECOGNITION_COMPONENTS && (
                        <div
                            className={
                                isFramed
                                    ? classNames(
                                          drawerStyles.cardRow,
                                          drawerStyles.cardRowTopDivider,
                                      )
                                    : styles.addRow
                            }
                        >
                            <Button
                                type="button"
                                variant="primary"
                                size="md"
                                leftIcon={<PlusIcon />}
                                onClick={() =>
                                    onDraftChange(addScheduleDraftRow(draft))
                                }
                            >
                                Add
                            </Button>
                        </div>
                    )}
            </div>
            {errorMessage && (
                <p className={styles.previewError}>{errorMessage}</p>
            )}
        </VStack>
    );
};
